import { useMemo } from "react";

import { SparklesIcon } from "@heroicons/react/24/solid";

import { IVehicle } from "../../models/Vehicle";
import { computeDismantleRecommendation } from "../../utils/dismantleRecommendation";
import PriceFormat from "../../utils/priceFormat";
import DismantleRecommendationChip from "./DismantleRecommendationChip";
import RowCard from "./RowCard";
import { buildMockVehicleIntelligence } from "./vehicleIntelligenceMockData";

interface WilmaIntelligenceCellProps {
  vehicle: IVehicle;
}

// Formats a number as GBP; returns a neutral em-dash when the value is
// missing so empty analytics don't masquerade as £0.00.
const money = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : PriceFormat(v);

const count = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : v.toLocaleString("en-GB");

export default function WilmaIntelligenceCell({
  vehicle,
}: WilmaIntelligenceCellProps) {
  // Deterministic demo bundle keyed off the vehicle id — used purely as a
  // per-field fallback until the backend populates the real analytics
  // fields. Memoised so each row's numbers stay stable across renders.
  const mock = useMemo(() => buildMockVehicleIntelligence(vehicle), [vehicle]);

  // Engine/export already have sensible proxies on the partial DTO — keep
  // using those first, then fall back to the mock.
  const parseOfferPrice = (raw: string | null | undefined) => {
    if (raw === null || raw === undefined || raw === "") return null;
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : null;
  };
  const engineOffer = parseOfferPrice(vehicle.enginesWorldwideEngineOffer);
  const vfeOffer = parseOfferPrice(vehicle.enginesWorldwideVfeOffer);
  const exportProxy =
    engineOffer !== null || vfeOffer !== null
      ? (engineOffer ?? 0) + (vfeOffer ?? 0)
      : null;

  // Each field prefers the real backend value, then any existing proxy, then
  // the deterministic mock. `hasRealValue` tracks whether _any_ real field
  // was populated so we can badge the card as "Demo data" when it is.
  let hasRealValue = false;
  const firstValue = (...vals: Array<number | null | undefined>) => {
    for (let i = 0; i < vals.length - 1; i += 1) {
      const v = vals[i];
      if (v !== null && v !== undefined) {
        hasRealValue = true;
        return v;
      }
    }
    return vals[vals.length - 1] as number;
  };

  const totalValueAllParts = firstValue(
    vehicle.totalValueAllParts,
    mock.totalValueAllParts
  );
  const projectedSales = firstValue(
    vehicle.projectedSales,
    mock.projectedSales
  );
  const valuePartsInStock = firstValue(
    vehicle.valuePartsInStock,
    mock.valuePartsInStock
  );
  const partsInStock = firstValue(
    vehicle.totalPartsInStock,
    vehicle.availablePartCount,
    mock.totalPartsInStock
  );

  const engineValue = firstValue(
    vehicle.engineValue,
    vehicle.ebayEnginePrice,
    mock.engineValue
  );
  const gearboxValue = firstValue(vehicle.gearboxValue, mock.gearboxValue);
  const exportValue = firstValue(
    vehicle.exportValue,
    exportProxy,
    mock.exportValue
  );

  const valuationTradePoor = firstValue(
    vehicle.valuationTradePoor,
    mock.valuationTradePoor
  );
  const recommendedPurchasePrice = firstValue(
    vehicle.recommendedPurchasePrice,
    mock.recommendedPurchasePrice
  );

  const isDemo = !hasRealValue;

  // Drive the headline score + verdict from the nine-component recommendation
  // model instead of the legacy `aiPartsIndex` so the cell reflects the
  // dismantle decision logic the operations team agreed on.
  const recommendation = useMemo(
    () =>
      computeDismantleRecommendation(vehicle, {
        totalValueAllParts,
        projectedSales,
        valuePartsInStock,
        totalPartsInStock: partsInStock,
        engineValue,
        gearboxValue,
        exportValue,
        valuationTradePoor,
      }),
    [
      vehicle,
      totalValueAllParts,
      projectedSales,
      valuePartsInStock,
      partsInStock,
      engineValue,
      gearboxValue,
      exportValue,
      valuationTradePoor,
    ]
  );

  const { total: score, verdict, breakdown } = recommendation;
  const scoreDisplay = score;
  const barWidth = Math.max(0, Math.min(100, score));

  return (
    <RowCard
      title="Wilma Intelligence"
      icon={SparklesIcon}
      ringClassName={verdict.ringClass}
      aside={
        <div className="flex items-center gap-1">
          {isDemo && (
            <span
              className="inline-flex items-center rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 ring-1 ring-inset ring-amber-200"
              title="Placeholder analytics — real values will appear once the backend publishes them."
            >
              Demo
            </span>
          )}
          <DismantleRecommendationChip
            score={score}
            verdict={verdict}
            breakdown={breakdown}
            isDemo={isDemo}
          />
        </div>
      }
    >
      {/* Wilma Recommendation — hero stat */}
      <div>
        <div className="flex items-baseline justify-between">
          <span className="text-[11px] font-medium text-slate-500">
            Wilma Recommendation
          </span>
          <span
            className={`text-2xl font-bold leading-none ${verdict.textClass}`}
          >
            {scoreDisplay}
            <span className="ml-0.5 text-xs font-medium text-slate-400">
              /100
            </span>
          </span>
        </div>
        <div
          className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-slate-100"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={barWidth}
        >
          <div
            className={`h-full rounded-full transition-all ${verdict.barClass}`}
            style={{ width: `${barWidth}%` }}
          />
        </div>
        <p className="mt-1 text-[10px] leading-snug text-slate-500">
          {verdict.description}
        </p>
      </div>

      {/* Financial metrics grid */}
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-slate-100 pt-2">
        <div>
          <dt className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
            Total Value
          </dt>
          <dd className="mt-0.5 text-sm font-semibold text-slate-800">
            {money(totalValueAllParts)}
          </dd>
        </div>
        <div>
          <dt className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
            Projected Sales
          </dt>
          <dd className="mt-0.5 text-sm font-semibold text-emerald-700">
            {money(projectedSales)}
          </dd>
        </div>
        <div>
          <dt className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
            Value In Stock
          </dt>
          <dd className="mt-0.5 text-sm font-semibold text-slate-800">
            {money(valuePartsInStock)}
          </dd>
        </div>
        <div>
          <dt className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
            Parts In Stock
          </dt>
          <dd className="mt-0.5 text-sm font-semibold text-slate-800">
            {count(partsInStock)}
          </dd>
        </div>
      </dl>

      {/* Component values — engine, gearbox, export */}
      <div className="mt-2 grid grid-cols-3 gap-1.5 border-t border-slate-100 pt-2">
        <div className="rounded-md bg-slate-50 px-1.5 py-1 text-center">
          <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
            Engine
          </p>
          <p className="mt-0.5 text-xs font-semibold text-slate-800">
            {money(engineValue)}
          </p>
        </div>
        <div className="rounded-md bg-slate-50 px-1.5 py-1 text-center">
          <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
            Gearbox
          </p>
          <p className="mt-0.5 text-xs font-semibold text-slate-800">
            {money(gearboxValue)}
          </p>
        </div>
        <div className="rounded-md bg-slate-50 px-1.5 py-1 text-center">
          <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
            Export
          </p>
          <p className="mt-0.5 text-xs font-semibold text-slate-800">
            {money(exportValue)}
          </p>
        </div>
      </div>

      {/* Buy-side callouts — salvage valuation + recommended purchase. Pinned
          to the bottom of the card so the row's cards all settle into the
          same baseline. */}
      <div className="mt-auto grid grid-cols-2 gap-2 pt-2">
        <div className="flex flex-col rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Salvage
          </span>
          <span className="text-sm font-bold text-slate-700">
            {money(valuationTradePoor)}
          </span>
        </div>
        <div className="flex flex-col rounded-md border border-primary-100 bg-primary-50/70 px-2 py-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-700">
            Rec. Purchase
          </span>
          <span className="text-sm font-bold text-primary-700">
            {money(recommendedPurchasePrice)}
          </span>
        </div>
      </div>
    </RowCard>
  );
}
